import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, addMonths, format } from 'date-fns';
import { companyMonthRange, entryDayKey } from '@/lib/timesheetTime';
import type { 
  TimeEntry, 
  TimeEntryWithProfile, 
  TimeEntryWithComments,
  TimeEntryComment,
  Profile, 
  TimesheetFilters,
  PaginatedTimeEntries,
  ApproveTimeEntryInput,
  EditTimeEntryInput,
  EditOwnTimeEntryInput,
  AddCommentInput,
  PayrollGenerationInput,
  PayrollExportInput,
  PayrollGenerationResult,
  CreateManualEntryInput,
  CreateBulkEntriesInput,
  BulkCreationResult,
  ExistingTimeEntryConflict,
  PayrollVerificationStatus
} from './types';

/**
 * Batch-fetch payroll verification status for time entries in a given month.
 * Returns a map of profile_id+date -> payroll status
 * 
 * Logic:
 * - Match time entries to payslips by profile_id and clock_in date within payslip date range
 * - Check the linked payroll_run status (completed vs rolled_back)
 * - Only approved entries can be payroll-verified
 */
async function batchFetchPayrollStatus(
  entries: TimeEntryWithProfile[],
  month: Date,
  timezone?: string | null
): Promise<Map<string, PayrollVerificationStatus>> {
  const result = new Map<string, PayrollVerificationStatus>();
  
  if (entries.length === 0) return result;
  
  // Get unique profile IDs from entries
  // Filter out null/undefined profile_ids (orphaned entries from deleted employees)
  // to prevent "invalid input syntax for type uuid: null" errors in PostgREST .in() filter
  const profileIds = [...new Set(
    entries.map(e => e.profile_id).filter((id): id is string => !!id)
  )];
  if (profileIds.length === 0) return result;
  
  // Calculate month boundaries for payslip lookup
  const monthStart = format(startOfMonth(month), 'yyyy-MM-dd');
  const monthEnd = format(startOfMonth(addMonths(month, 1)), 'yyyy-MM-dd');
  
  try {
    // Fetch payslips with their payroll run status for the month range
    // Using a single batch query to avoid N+1
    const { data: payslips, error } = await supabase
      .from('payslips')
      .select(`
        id,
        profile_id,
        start_date,
        end_date,
        created_at,
        payroll_run_id,
        payroll_runs!inner (
          id,
          status
        )
      `)
      .in('profile_id', profileIds)
      .lte('start_date', monthEnd)
      .gte('end_date', monthStart)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('[Payroll Status] Error fetching payslips:', error);
      return result;
    }
    
    if (!payslips || payslips.length === 0) {
      return result;
    }
    
    // Build a map of profile_id -> latest payslip info per date range
    // Key: "profile_id:date" to match entries to payslips
    const payslipMap = new Map<string, { status: string; payslipId: string }>();
    
    for (const payslip of payslips) {
      const runStatus = (payslip.payroll_runs as any)?.status || 'unknown';
      
      // Iterate the payslip range in UTC. `start_date`/`end_date` are plain
      // calendar dates; parsing/formatting them in the browser locale shifted
      // the whole range by a day for negative-offset browsers.
      const startDate = new Date(`${payslip.start_date}T00:00:00Z`);
      const endDate = new Date(`${payslip.end_date}T00:00:00Z`);

      for (let d = new Date(startDate); d <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
        const dateKey = `${payslip.profile_id}:${d.toISOString().slice(0, 10)}`;
        
        // Only set if not already set (first = most recent due to order by created_at desc)
        if (!payslipMap.has(dateKey)) {
          payslipMap.set(dateKey, {
            status: runStatus,
            payslipId: payslip.id
          });
        }
      }
    }
    
    // Map entries to their payroll status
    for (const entry of entries) {
      // Only approved entries can show payroll status
      if (entry.status !== 'approved') {
        result.set(entry.id, null);
        continue;
      }
      
      const entryDate = entryDayKey(entry.clock_in, timezone);
      const lookupKey = `${entry.profile_id}:${entryDate}`;
      const payslipInfo = payslipMap.get(lookupKey);
      
      if (!payslipInfo) {
        result.set(entry.id, null);
      } else if (payslipInfo.status === 'completed') {
        result.set(entry.id, 'verified');
      } else if (payslipInfo.status === 'rolled_back') {
        result.set(entry.id, 'rolled_back');
      } else {
        result.set(entry.id, null);
      }
    }
    
    return result;
  } catch (err) {
    console.error('[Payroll Status] Exception:', err);
    return result;
  }
}

export const timesheetService = {
  // Get time entries for a specific user (MyTimesheets)
  async getMyTimeEntries(profileId: string, month?: Date, timezone?: string | null): Promise<TimeEntry[]> {
    // Ensure user context is set before making queries that involve RLS policies
    await supabase.rpc('ensure_user_context');
    
    let query = supabase
      .from('time_entries')
      .select('id, profile_id, clock_in, clock_out, status, approved_at, approved_by_id, edit_comment, created_at, updated_at, entry_type')
      .eq('profile_id', profileId);

    // Apply month filter if provided
    if (month) {
      const { startISO, endISO } = companyMonthRange(month, timezone);
      query = query.gte('clock_in', startISO).lt('clock_in', endISO);
    }

    const { data, error } = await query.order('clock_in', { ascending: false });

    if (error) {
      console.error('Error fetching my time entries:', error);
      throw new Error(`Failed to fetch time entries: ${error.message}`);
    }

    return (data || []) as TimeEntry[];
  },

  // Get time entries for company (ManagerTimesheets)
  async getCompanyTimeEntries(filters: TimesheetFilters): Promise<{ data: TimeEntryWithProfile[]; totalCount: number }> {
    try {
      // Ensure user context is set before making queries that involve RLS policies
      // MUST run first - required for RLS per architecture/security/rls-user-context-standard
      await supabase.rpc('ensure_user_context');
      
      // Calculate month range for filtering, anchored to the company timezone
      // so fetched rows match the dates rendered on screen.
      const { startISO: monthStart, endISO: monthEnd } = companyMonthRange(
        filters.month,
        filters.timezone
      );

      // Pagination defaults
      const limit = filters.limit || 50;
      const page = filters.page || 0;
      const from = page * limit;
      const to = from + limit - 1;

      // When payroll status filter is active, use server-side RPC to get filtered IDs
      if (filters.payrollStatus) {
        return this._getCompanyTimeEntriesByPayrollFilter(filters, monthStart, monthEnd, limit, page, from, to);
      }

      // Build time_entries query with server-side filters
      let timeQuery = supabase
        .from('time_entries')
        .select('id, profile_id, clock_in, clock_out, status, approved_at, approved_by_id, edit_comment, created_at, updated_at, entry_type', { count: 'exact' })
        .gte('clock_in', monthStart)
        .lt('clock_in', monthEnd);

      // Server-side employee filter (by profile_id)
      if (filters.employeeId && filters.employeeId !== 'all') {
        timeQuery = timeQuery.eq('profile_id', filters.employeeId);
      }

      // Server-side status filter
      if (filters.status && filters.status !== 'all') {
        timeQuery = timeQuery.eq('status', filters.status);
      }

      // Apply pagination and ordering
      timeQuery = timeQuery.order('clock_in', { ascending: false }).range(from, to);

      // Run time_entries (with count) and profiles in parallel
      const [timeResult, profilesResult] = await Promise.all([
        timeQuery,
        supabase.rpc('list_company_profiles')
      ]);

      // Handle time entries error (critical - must throw)
      if (timeResult.error) {
        console.error('Error fetching time entries:', timeResult.error);
        throw new Error(`Failed to fetch time entries: ${timeResult.error.message}`);
      }

      const timeData = timeResult.data;
      const totalCount = timeResult.count || 0;

      return { data: await this._enrichTimeEntries(timeData || [], profilesResult), totalCount };
    } catch (error) {
      console.error('Error in getCompanyTimeEntries:', error);
      throw error;
    }
  },

  // Server-side payroll-filtered query path
  async _getCompanyTimeEntriesByPayrollFilter(
    filters: TimesheetFilters,
    monthStart: string,
    monthEnd: string,
    limit: number,
    page: number,
    from: number,
    to: number
  ): Promise<{ data: TimeEntryWithProfile[]; totalCount: number }> {
    // Call the server-side RPC that filters by payroll status
    const { data: rpcResult, error: rpcError } = await supabase.rpc('get_time_entries_by_payroll_filter', {
      p_month_start: monthStart,
      p_month_end: monthEnd,
      p_payroll_status: filters.payrollStatus!,
      p_employee_id: filters.employeeId && filters.employeeId !== 'all' ? filters.employeeId : null,
      p_limit: limit,
      p_offset: from,
    });

    if (rpcError) {
      console.error('Error fetching payroll-filtered entries:', rpcError);
      throw new Error(`Failed to fetch payroll-filtered entries: ${rpcError.message}`);
    }

    const parsed = rpcResult as { entry_ids: string[]; total_count: number } | null;
    const entryIds: string[] = parsed?.entry_ids || [];
    const totalCount: number = parsed?.total_count || 0;

    if (entryIds.length === 0) {
      return { data: [], totalCount };
    }

    // Fetch the actual entry data + profiles for the filtered IDs
    const [timeResult, profilesResult] = await Promise.all([
      supabase
        .from('time_entries')
        .select('id, profile_id, clock_in, clock_out, status, approved_at, approved_by_id, edit_comment, created_at, updated_at, entry_type')
        .in('id', entryIds)
        .order('clock_in', { ascending: false }),
      supabase.rpc('list_company_profiles')
    ]);

    if (timeResult.error) {
      console.error('Error fetching time entries by IDs:', timeResult.error);
      throw new Error(`Failed to fetch time entries: ${timeResult.error.message}`);
    }

    return { data: await this._enrichTimeEntries(timeResult.data || [], profilesResult), totalCount };
  },

  // Shared enrichment: profiles + attendance flags
  async _enrichTimeEntries(
    timeData: any[],
    profilesResult: { data: any; error: any }
  ): Promise<TimeEntryWithProfile[]> {
    // Handle profiles error with graceful degradation (non-critical)
    if (profilesResult.error) {
      console.warn('Warning: Failed to fetch profiles, using fallback:', profilesResult.error);
    }
    const profilesData = profilesResult.error ? [] : (profilesResult.data as any[]);

    // Create a map of profiles by ID for quick lookup
    const profilesMap = new Map(profilesData?.map((p: any) => [p.id, p]) || []);

    // Get attendance flags for these entries
    const entryIds = timeData.map(e => e.id);
    let flagsMap = new Map<string, Array<any>>();
    
    if (entryIds.length > 0) {
      const { data: flagsData, error: flagsError } = await supabase
        .from('attendance_event_flags')
        .select('id, time_entry_id, flag_type, flag_severity, flag_reason, resolved_at')
        .in('time_entry_id', entryIds)
        .is('resolved_at', null);

      if (!flagsError && flagsData) {
        flagsData.forEach(flag => {
          if (flag.time_entry_id) {
            if (!flagsMap.has(flag.time_entry_id)) {
              flagsMap.set(flag.time_entry_id, []);
            }
            flagsMap.get(flag.time_entry_id)!.push(flag);
          }
        });
      }
    }
    
    // Transform the data to match our interface
    return timeData.map(entry => {
      const profile = profilesMap.get(entry.profile_id);
      const flags = flagsMap.get(entry.id) || [];
      return {
        ...entry,
        profile: profile ? {
          id: profile.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          avatar_url: (profile as any).avatar_url || null
        } : (entry.profile_id ? {
          // profile_id exists but profile not in scope (archived/cross-tenant) — preserve legacy fallback
          id: entry.profile_id,
          first_name: null,
          last_name: null,
          email: 'unknown@example.com',
          avatar_url: null
        } : null), // true orphan (profile_id is NULL) — let UI render "Former employee" and lock actions
        attendance_flags: flags
      } as TimeEntryWithProfile;
    });
  },

  // Get company profiles for employee filter
  async getCompanyProfiles(): Promise<Profile[]> {
    const { data, error } = await supabase.rpc('list_company_profiles');

    if (error) {
      console.error('Error fetching company profiles:', error);
      throw new Error(`Failed to fetch company profiles: ${error.message}`);
    }

    // Add missing fields for compatibility and sort by first name
    const sortedData = (data || []).map(profile => ({
      ...profile,
      avatar_url: null, // Add missing avatar_url field
      company_id: null  // Add missing company_id field
    })).sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
    
    return sortedData;
  },

  // Approve or reject a time entry
  async approveTimeEntry({ entryId, status }: ApproveTimeEntryInput): Promise<{ profileId: string; companyId: string } | void> {
    // First get the time entry to get profile_id and company_id for notification
    const { data: entryData } = await supabase
      .from('time_entries')
      .select('profile_id, company_id')
      .eq('id', entryId)
      .single();

    const { error } = await supabase.rpc('approve_time_entry', {
      entry_id: entryId,
      new_status: status
    });

    if (error) {
      console.error('Error approving time entry:', error);
      throw new Error(`Failed to ${status} time entry: ${error.message}`);
    }

    // Return entry info for notification
    if (entryData) {
      return { profileId: entryData.profile_id, companyId: entryData.company_id };
    }
  },

  // Revert time entry status (admin only)
  async revertTimeEntry(entryId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_revert_timesheet_status', {
      time_entry_id: entryId
    });

    if (error) {
      console.error('Error reverting time entry:', error);
      throw new Error(`Failed to revert time entry: ${error.message}`);
    }
  },

  // Edit time entry clock in and/or clock out time.
  // The optional comment is written inside the same RPC transaction, so a
  // failed comment can never leave a committed time change behind.
  async editTimeEntry({ entryId, clockIn, clockOut, editComment, addComment }: EditTimeEntryInput): Promise<void> {
    const params: any = { entry_id: entryId.toString() };

    if (clockIn) {
      params.new_clock_in = new Date(clockIn).toISOString();
    }

    if (clockOut) {
      params.new_clock_out = new Date(clockOut).toISOString();
    }

    if (editComment && editComment.trim()) {
      params.p_edit_comment = editComment.trim();
    }

    if (addComment && addComment.trim()) {
      params.p_comment = addComment.trim();
    }

    const { error } = await supabase.rpc('edit_time_entry_full', params);

    if (error) {
      console.error('Error editing time entry:', error);
      throw new Error(`Failed to edit time entry: ${error.message}`);
    }
  },

  // Get comments for a specific time entry
  async getTimeEntryComments(entryId: string): Promise<TimeEntryComment[]> {
    const { data, error } = await supabase.rpc('get_time_entry_comments', {
      p_time_entry_id: entryId
    });

    if (error) {
      console.error('Error fetching time entry comments:', error);
      throw new Error(`Failed to fetch comments: ${error.message}`);
    }

    return data || [];
  },

  // Add a comment to a time entry
  async addTimeEntryComment({ entryId, commentText }: AddCommentInput): Promise<string> {
    const { data, error } = await supabase.rpc('add_time_entry_comment', {
      p_time_entry_id: entryId,
      p_comment_text: commentText
    });

    if (error) {
      console.error('Error adding time entry comment:', error);
      throw new Error(`Failed to add comment: ${error.message}`);
    }

    return data; // Returns the comment ID
  },

// Enhanced method to get time entries with comment indicators and payroll status
  async getCompanyTimeEntriesWithComments(filters: TimesheetFilters): Promise<PaginatedTimeEntries> {
    // Get paginated time entries
    const { data: timeEntries, totalCount } = await this.getCompanyTimeEntries(filters);
    
    const limit = filters.limit || 50;
    const page = filters.page || 0;
    const hasMore = (page + 1) * limit < totalCount;

    // Get comment counts for all entries
    const entryIds = timeEntries.map(entry => entry.id);
    
    // If no entries, return empty
    if (entryIds.length === 0) {
      return {
        entries: [],
        totalCount,
        hasMore,
      };
    }

    // Fetch comments and payroll status in parallel for efficiency
    const [commentMap, payrollStatusMap] = await Promise.all([
      (async () => {
        const map = new Map<string, boolean>();
        try {
          const { data, error } = await supabase.rpc('get_time_entries_have_comments', {
            p_entry_ids: entryIds
          });
          
          if (error) {
            console.warn('⚠️ Error batch checking comments:', error);
            entryIds.forEach(id => map.set(id, false));
            return map;
          }
          
          (data || []).forEach((row: { entry_id: string; has_comments: boolean }) => {
            map.set(row.entry_id, row.has_comments);
          });
          
          entryIds.forEach(id => {
            if (!map.has(id)) {
              map.set(id, false);
            }
          });
          
          return map;
        } catch (err) {
          console.warn('⚠️ Exception batch checking comments:', err);
          entryIds.forEach(id => map.set(id, false));
          return map;
        }
      })(),
      batchFetchPayrollStatus(timeEntries, filters.month, filters.timezone)
    ]);

    // Combine all data into final result
    const entries = timeEntries.map(entry => ({
      ...entry,
      has_comments: commentMap.get(entry.id) || false,
      payroll_status: payrollStatusMap.get(entry.id) || null
    }));

    return { entries, totalCount, hasMore };
  },

  // Generate payroll (admin only)
  async generatePayroll({ startDate, endDate }: PayrollGenerationInput): Promise<PayrollGenerationResult> {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('generate-payroll', {
      body: {
        start_date: startDate,
        end_date: endDate,
      },
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });

    if (error) {
      console.error('Error generating payroll:', error);
      throw new Error(`Failed to generate payroll: ${error.message}`);
    }

    return data;
  },

  // Export payroll data (admin only)
  async exportPayrollData({ startDate, endDate }: PayrollExportInput): Promise<Blob> {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`https://gesmjeokchbtbxthrizm.supabase.co/functions/v1/export-payroll-data`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }

    return response.blob();
  },

  // Create manual time entry
  async createManualTimeEntry({ clockIn, clockOut, reason }: CreateManualEntryInput): Promise<string> {
    const { data, error } = await supabase.rpc('create_manual_time_entry', {
      p_clock_in: new Date(clockIn).toISOString(),
      p_clock_out: new Date(clockOut).toISOString(),
      p_reason: reason
    });

    if (error) {
      console.error('Error creating manual time entry:', error);
      throw new Error(`Failed to create manual entry: ${error.message}`);
    }

    return data; // Returns entry ID
  },

  // Edit own time entry (staff self-service - uses permission-protected RPC)
  async editOwnTimeEntry({ entryId, clockIn, clockOut, editComment }: EditOwnTimeEntryInput): Promise<void> {
    const { error } = await supabase.rpc('edit_own_time_entry', {
      p_entry_id: entryId,
      p_clock_in: clockIn ? new Date(clockIn).toISOString() : null,
      p_clock_out: clockOut ? new Date(clockOut).toISOString() : null,
      p_edit_comment: editComment || null
    });

    if (error) {
      console.error('Error editing own time entry:', error);
      throw new Error(`Failed to edit time entry: ${error.message}`);
    }
  },

  // Create bulk time entries (manager-only, for backfilling employee timesheets)
  async createBulkTimeEntries(input: CreateBulkEntriesInput): Promise<BulkCreationResult> {
    // Cast entries to JSON-compatible format for Supabase RPC
    const entriesJson = JSON.parse(JSON.stringify(input.entries));

    const { data, error } = await supabase.rpc('create_bulk_time_entries', {
      p_profile_id: input.profileId,
      p_entries: entriesJson,
      p_reason: input.reason,
      p_status: input.status
    });

    if (error) {
      console.error('Error creating bulk time entries:', error);
      throw new Error(`Bulk creation failed: ${error.message}`);
    }

    return data as unknown as BulkCreationResult;
  },

  // Export all matching time entries (non-paginated) for CSV export
  async exportTimesheetEntries(params: {
    month: Date;
    employeeId?: string;
    status?: 'pending' | 'approved' | 'rejected';
    timezone?: string | null;
  }): Promise<TimeEntryWithProfile[]> {
    await supabase.rpc('ensure_user_context');

    const { startISO: monthStart, endISO: monthEnd } = companyMonthRange(
      params.month,
      params.timezone
    );

    let query = supabase
      .from('time_entries')
      .select('id, profile_id, clock_in, clock_out, status, approved_at, approved_by_id, edit_comment, created_at, updated_at, entry_type')
      .gte('clock_in', monthStart)
      .lt('clock_in', monthEnd);

    if (params.employeeId) {
      query = query.eq('profile_id', params.employeeId);
    }
    if (params.status) {
      query = query.eq('status', params.status);
    }

    query = query.order('clock_in', { ascending: true });

    const [timeResult, profilesResult] = await Promise.all([
      query,
      supabase.rpc('list_company_profiles'),
    ]);

    if (timeResult.error) {
      throw new Error(`Export failed: ${timeResult.error.message}`);
    }

    return this._enrichTimeEntries(timeResult.data || [], profilesResult);
  },

  // Check for existing time entries that would conflict with bulk entries
  async checkBulkEntryConflicts(
    profileId: string, 
    dates: string[],
    timezone?: string | null
  ): Promise<ExistingTimeEntryConflict[]> {
    if (dates.length === 0) return [];
    
    // Get min and max dates for query range
    const sortedDates = [...dates].sort();
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];
    
  // Query existing entries for this profile within the date range
    // Exclude rejected entries - they don't count as conflicts
    const { data, error } = await supabase
      .from('time_entries')
      .select('id, clock_in, clock_out, status')
      .eq('profile_id', profileId)
      .neq('status', 'rejected')
      .gte('clock_in', `${startDate}T00:00:00Z`)
      .lt('clock_in', `${endDate}T23:59:59Z`);

    if (error) {
      console.error('Error checking bulk entry conflicts:', error);
      throw new Error(`Failed to check conflicts: ${error.message}`);
    }

    // Map entries to conflicts using the company-timezone day bucket so the
    // preview agrees with what the timesheet grid renders.
    return (data || []).map(entry => ({
      date: entryDayKey(entry.clock_in, timezone),
      clock_in: entry.clock_in,
      clock_out: entry.clock_out
    }));
  }
};
